/**
 * Scopes CSS selectors to a specific container class to prevent style leakage
 */
function scopeCSS(css: string, scopeClass: string): string {
  if (!css) return ''
  
  // Remove comments
  css = css.replace(/\/\*[\s\S]*?\*\//g, '')
  
  // Split by closing brace to handle multiple rules
  const rules = css.split('}')
  
  const scopedRules = rules.map(rule => {
    if (!rule.trim()) return ''
    
    const parts = rule.split('{')
    if (parts.length !== 2) return rule + '}'
    
    const selectors = parts[0].trim()
    const declarations = parts[1].trim()
    
    if (!selectors || !declarations) return rule + '}'
    
    // Split selectors by comma
    const selectorList = selectors.split(',').map(s => s.trim())
    
    // Scope each selector
    const scopedSelectors = selectorList.map(selector => {
      // Skip @-rules (like @media, @keyframes)
      if (selector.startsWith('@')) return selector
      
      // Don't scope :root, html, or body
      if (selector.match(/^(html|body|:root)\b/)) {
        return `.${scopeClass} ${selector.replace(/^(html|body|:root)\b/, '')}`
      }
      
      // Add scope class as ancestor
      return `.${scopeClass} ${selector}`
    })
    
    return `${scopedSelectors.join(', ')} { ${declarations} }`
  }).filter(Boolean)
  
  return scopedRules.join('\n')
}

/**
 * Sanitizes article HTML to prevent CSS leakage while preserving article styling
 * - Extracts and scopes <style> tags to prevent global pollution
 * - Removes script tags for security
 * - Removes metadata that might conflict (reading time, dates)
 * - Removes duplicate title (first h1) and featured image if present
 * - Preserves inline styles (they're safe within the isolated container)
 */
export function sanitizeArticleHtml(html: string, options?: { removeFirstH1?: boolean; removeFirstImage?: boolean; featuredImageUrl?: string }): string {
  if (!html) return '<p>No content available</p>'
  
  const { removeFirstH1 = true, removeFirstImage = true, featuredImageUrl } = options || {}
  
  let sanitized = html
  let extractedStyles = ''
  
  // Extract all <style> tags and their contents
  sanitized = sanitized.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (match, styleContent) => {
    extractedStyles += styleContent + '\n'
    return '' // Remove the style tag from HTML
  })
  
  // Remove all <script> tags for security
  sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
  
  // Remove duplicate title (first h1) if option is enabled
  if (removeFirstH1) {
    sanitized = sanitized.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '')
  }
  
  // Remove duplicate featured image (first img or first element with background image) if option is enabled
  if (removeFirstImage) {
    // First, remove header sections that might contain featured images
    sanitized = sanitized.replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
    
    // Remove divs containing featured images (check for featured image URL if provided)
    if (featuredImageUrl) {
      // Escape the URL for regex
      const escapedUrl = featuredImageUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      
      // Remove divs that contain the featured image URL, but preserve image-container divs (content images)
      sanitized = sanitized.replace(new RegExp(`<div[^>]*(?!class=["'][^"']*image-container)[^>]*>[\\s\\S]*?<img[^>]*src=["']${escapedUrl}["'][^>]*>[\\s\\S]*?<\\/div>`, 'gi'), '')
      
      // Also remove standalone img tags with the featured image URL
      // But first, mark image-container divs so we don't remove images inside them
      const imageContainerPattern = /<div[^>]*class=["'][^"']*image-container[^"']*["'][^>]*>[\s\S]*?<\/div>/gi
      const containerMatches = [...sanitized.matchAll(imageContainerPattern)]
      const protectedRanges: Array<[number, number]> = []
      
      containerMatches.forEach(match => {
        if (match.index !== undefined) {
          protectedRanges.push([match.index, match.index + match[0].length])
        }
      })
      
      // Now remove featured image URLs, but skip if inside protected ranges
      const featuredImagePattern = new RegExp(`<img[^>]*src=["']${escapedUrl}["'][^>]*>`, 'gi')
      let match
      const toRemove: Array<[number, number]> = []
      
      while ((match = featuredImagePattern.exec(sanitized)) !== null) {
        const matchStart = match.index
        const matchEnd = matchStart + match[0].length
        const isProtected = protectedRanges.some(([start, end]) => matchStart >= start && matchEnd <= end)
        
        if (!isProtected) {
          toRemove.push([matchStart, matchEnd])
        }
      }
      
      // Remove matches in reverse order to preserve indices
      toRemove.reverse().forEach(([start, end]) => {
        sanitized = sanitized.substring(0, start) + sanitized.substring(end)
      })
    }
    
    // Remove divs with "featured" in class name
    sanitized = sanitized.replace(/<div[^>]*class="[^"]*featured[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
    
    // Remove divs with hero image styling (centered, large margins) - be more specific to avoid removing content images
    // Only match if BOTH text-align: center AND margin: 30px are present (not 2rem or other values)
    sanitized = sanitized.replace(/<div[^>]*style="[^"]*text-align:\s*center[^"]*margin:\s*30px[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '')
    
    // More precise: Only remove the first img tag if it's NOT inside an image-container div (content images)
    // Content images are wrapped in divs with class="image-container" and should be preserved
    const firstImageMatch = sanitized.match(/<img[^>]*>/i)
    if (firstImageMatch && !featuredImageUrl) {
      // If no featuredImageUrl provided, check if first image is a content image
      const firstImageIndex = sanitized.indexOf(firstImageMatch[0])
      const beforeImage = sanitized.substring(0, firstImageIndex)
      
      // Check if there's an image-container div opening tag before this image
      const imageContainerOpenings = [...beforeImage.matchAll(/<div[^>]*class=["'][^"']*image-container[^"']*["'][^>]*>/gi)]
      
      // Find the closest opening tag before the image
      let isInImageContainer = false
      for (const match of imageContainerOpenings) {
        const openIndex = beforeImage.lastIndexOf(match[0])
        // Check if there's a closing div tag between the opening and the image
        const between = sanitized.substring(openIndex + match[0].length, firstImageIndex)
        const closingDivCount = (between.match(/<\/div>/g) || []).length
        const openingDivCount = (between.match(/<div[^>]*>/gi) || []).length
        
        // If opening divs >= closing divs, the image is still inside the container
        if (openingDivCount >= closingDivCount) {
          isInImageContainer = true
          break
        }
      }
      
      // Only remove if it's NOT a content image
      if (!isInImageContainer) {
        sanitized = sanitized.replace(/<img[^>]*>/i, '')
      }
    }
    
    // Remove any remaining divs that wrap a single image at the start (but NEVER remove image-container divs)
    // Check if first div is an image-container - if so, skip removal
    const firstDivMatch = sanitized.match(/^<div[^>]*>/i)
    if (firstDivMatch && !firstDivMatch[0].includes('image-container')) {
      // Only remove if it contains an image and is NOT an image-container
      sanitized = sanitized.replace(/^(<div[^>]*>[\s\S]*?<img[^>]*>[\s\S]*?<\/div>)/i, (match) => {
        // Double-check it's not an image-container
        if (!match.includes('image-container')) {
          return ''
        }
        return match
      })
    }
  }
  
  // Remove common metadata patterns that might conflict
  // Remove things like "10 min read", "5 minute read", etc.
  sanitized = sanitized.replace(/\d+\s*(?:min|minute|minutes)\s*read/gi, '')
  
  // Remove standalone bullet points or dots used in metadata
  sanitized = sanitized.replace(/\s*[•·]\s*/g, ' ')
  
  // Remove stray asterisks from markdown conversion artifacts
  // Remove standalone asterisks with spaces: " * ", " *", "* "
  sanitized = sanitized.replace(/\s+\*\s+/g, ' ')
  sanitized = sanitized.replace(/\s+\*/g, ' ')
  sanitized = sanitized.replace(/\*\s+/g, ' ')
  
  // Remove asterisks that appear at the start or end of text nodes (but preserve them in HTML tags)
  // This regex matches asterisks that are not part of HTML tags or attributes
  sanitized = sanitized.replace(/>\s*\*\s*</g, '><')
  sanitized = sanitized.replace(/>\s*\*/g, '>')
  sanitized = sanitized.replace(/\*\s*</g, '<')
  
  // Remove asterisks that appear alone in paragraphs or text content
  sanitized = sanitized.replace(/<p[^>]*>\s*\*\s*<\/p>/gi, '')
  sanitized = sanitized.replace(/<p[^>]*>([^<]*)\*\s*([^<]*)<\/p>/gi, '<p>$1$2</p>')
  sanitized = sanitized.replace(/<p[^>]*>([^<]*)\s+\*\s+([^<]*)<\/p>/gi, '<p>$1 $2</p>')
  
  // Remove asterisks that appear at the beginning or end of text content within tags
  sanitized = sanitized.replace(/(>)([^<]*?)\s*\*\s*([^<]*?)(<)/g, '$1$2$3$4')
  
  // Clean up any resulting double spaces
  sanitized = sanitized.replace(/\s{2,}/g, ' ')
  
  // Scope the extracted CSS to the article container
  const scopedStyles = scopeCSS(extractedStyles, 'article-content-scope')
  
  // Re-inject scoped styles at the beginning
  if (scopedStyles) {
    sanitized = `<style>${scopedStyles}</style>${sanitized}`
  }
  
  return sanitized
}

/**
 * Gets safe CSS isolation styles for article content containers
 * Also includes the scoping class name
 */
export function getArticleIsolationStyles(): React.CSSProperties {
  return {
    isolation: 'isolate',
    contain: 'layout style',
    position: 'relative',
    zIndex: 1,
  }
}

/**
 * Gets the class name for article content scoping
 */
export function getArticleScopeClass(): string {
  return 'article-content-scope'
}

